import React, {useState} from "react";
import {useFieldArray, useFormContext, useWatch} from "react-hook-form";
import {Box, Colors, Text} from "@bounce/shared/ui";
import {ReachPromoTemplateRef} from "../../../apis/requests/promos/types";
import {promoDayLabels} from "./dates";
import {FieldLabel, FormCard} from "./FormCard";
import {
  DayAction,
  dayAction,
  forcedTemplateIds,
  promoWindowDays,
  reminderForDay,
  ReminderFormValues,
  todayDateInput,
  withDayAction,
  withDayReminder,
  withForcedTemplates,
} from "./helpers";
import PromoDayPlan from "./PromoDayPlan";
import PromoTemplatesSelect, {PromoTemplateOption} from "./PromoTemplatesSelect";
import {PromoFormValues} from "./schema";

const COMMS_HINT =
  "Templates to force on promo days. For each day, let the model choose (regular daily comms with the regular templates), send nothing, or force: pick its templates (email and SMS, MMS optional), the model picks the channel, and you can add a reminder on the other channel after a few hours.";
const TEMPLATES_LABEL = "Templates";
const DAY_PLAN_LABEL = "Day plan";
const OPEN_ENDED_DAY_OPTIONS = 30;
const REMINDER_HOURS_ERROR = "Reminders need a whole number of hours (at least 1)";

type Props = {
  initialTemplates: ReachPromoTemplateRef[];
};

const CommsCard = ({initialTemplates}: Props) => {
  const {
    control,
    setValue,
    formState: {errors},
  } = useFormContext<PromoFormValues>();
  const {fields, replace} = useFieldArray({control, name: "templates"});
  const [promoWindow, startsAt, endsAt, startMode, startWeekday, templates, noSendDays, forceDays, reminders] =
    useWatch({
      control,
      name: [
        "window",
        "starts_at",
        "ends_at",
        "start_mode",
        "start_weekday",
        "templates",
        "no_send_days",
        "force_days",
        "reminders",
      ],
    });
  const windowDays = promoWindowDays({window: promoWindow, starts_at: startsAt, ends_at: endsAt});
  const dayLabels = promoDayLabels(
    {start_mode: startMode, starts_at: startsAt, start_weekday: startWeekday},
    todayDateInput(),
    windowDays ?? OPEN_ENDED_DAY_OPTIONS
  );
  const [templateById, setTemplateById] = useState(
    () => new Map<number, PromoTemplateOption>(initialTemplates.map((template) => [template.id, template]))
  );
  const plan = {templates, no_send_days: noSendDays, force_days: forceDays, reminders};
  const setDirty = {shouldDirty: true, shouldValidate: true};

  const onTemplatesChange = (selectedTemplates: PromoTemplateOption[]) => {
    setTemplateById(
      (current) => new Map([...current, ...selectedTemplates.map((template) => [template.id, template] as const)])
    );
    const daysByTemplateId = new Map(fields.map((link) => [link.template_id, link.days_since_start]));
    const nextTemplates = selectedTemplates.map((template) => ({
      template_id: template.id,
      days_since_start: daysByTemplateId.get(template.id) ?? [],
    }));
    replace(nextTemplates);
  };

  const onDayActionChange = (day: number, action: DayAction) => {
    const nextPlan = withDayAction(plan, day, action);
    replace(nextPlan.templates);
    setValue("no_send_days", nextPlan.no_send_days, setDirty);
    setValue("force_days", nextPlan.force_days, setDirty);
    setValue("reminders", nextPlan.reminders, setDirty);
  };

  const onForcedTemplatesChange = (day: number, templateIds: number[]) =>
    replace(withForcedTemplates(plan, day, templateIds));

  const onReminderChange = (day: number, reminder: ReminderFormValues | null) =>
    setValue("reminders", withDayReminder(plan, day, reminder), setDirty);

  return (
    <FormCard title="Communications" hint={COMMS_HINT}>
      <Box>
        <FieldLabel>{TEMPLATES_LABEL}</FieldLabel>
        <PromoTemplatesSelect
          value={fields.map((link) => link.template_id)}
          onChange={onTemplatesChange}
          initialTemplates={initialTemplates}
        />
        {errors.templates?.message && (
          <Text size={12} sx={{color: Colors.red._900, mt: 1}}>
            {errors.templates.message}
          </Text>
        )}
      </Box>
      <Box>
        <FieldLabel>{DAY_PLAN_LABEL}</FieldLabel>
        <PromoDayPlan
          dayLabels={dayLabels}
          templateIds={fields.map((link) => link.template_id)}
          templateById={templateById}
          actionForDay={(day) => dayAction(plan, day)}
          onChange={onDayActionChange}
          forcedTemplatesFor={(day) => forcedTemplateIds(plan, day)}
          onForcedTemplatesChange={onForcedTemplatesChange}
          reminderForDay={(day) => reminderForDay(plan, day)}
          onReminderChange={onReminderChange}
        />
        {errors.force_days?.message && (
          <Text size={12} sx={{color: Colors.red._900, mt: 1}}>
            {errors.force_days.message}
          </Text>
        )}
        {errors.reminders?.some?.((reminder) => reminder?.delay_hours) && (
          <Text size={12} sx={{color: Colors.red._900, mt: 1}}>
            {REMINDER_HOURS_ERROR}
          </Text>
        )}
        {errors.no_send_days?.message && (
          <Text size={12} sx={{color: Colors.red._900, mt: 1}}>
            {errors.no_send_days.message}
          </Text>
        )}
      </Box>
    </FormCard>
  );
};

export default CommsCard;
